import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { logAdminAction } from "@/lib/logAdminAction";
import { useAuth } from "@/contexts/AuthContext";

interface ImpersonationState {
  active: boolean;
  adminId: string;
  adminEmail: string;
  tenantId: string;
  tenantName: string;
  timestamp: string;
}

interface ImpersonationContextType {
  impersonation: ImpersonationState | null;
  startImpersonation: (tenantId: string, tenantName: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationContextType>({
  impersonation: null,
  startImpersonation: async () => {},
  stopImpersonation: async () => {},
});

const STORAGE_KEY = "cx_impersonation";

function loadState(): ImpersonationState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return null;
  }
}

function saveState(state: ImpersonationState | null) {
  if (state) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } else {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(loadState);

  const startImpersonation = useCallback(
    async (tenantId: string, tenantName: string) => {
      if (!user) return;
      const state: ImpersonationState = {
        active: true,
        adminId: user.id,
        adminEmail: user.email ?? "unknown",
        tenantId,
        tenantName,
        timestamp: new Date().toISOString(),
      };
      saveState(state);
      setImpersonation(state);
      await logAdminAction({
        action_type: "impersonate_start",
        tenant_id: tenantId,
        tenant_name: tenantName,
        details: { admin_email: user.email },
      });
    },
    [user]
  );

  const stopImpersonation = useCallback(async () => {
    const current = impersonation;
    saveState(null);
    setImpersonation(null);
    if (current) {
      await logAdminAction({
        action_type: "impersonate_end",
        tenant_id: current.tenantId,
        tenant_name: current.tenantName,
        details: { admin_email: current.adminEmail },
      });
    }
  }, [impersonation]);

  return (
    <ImpersonationContext.Provider value={{ impersonation, startImpersonation, stopImpersonation }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  return useContext(ImpersonationContext);
}
